import type { TFunction } from 'i18next';
import type { NotificationItem } from '@/types/notification';
import { formatBelgianDate } from '@/utils/date';

/**
 * Extracts the contract's display name from a backend-generated notification title.
 *
 * The backend stores the fully-formatted (English or Dutch) sentence as `title`; this function
 * reverse-parses out just the contract name so it can be re-inserted into a locale-specific
 * i18n template via {@link getNotificationTitle}/{@link getNotificationMessage}.
 *
 * @param rawTitle - The raw notification title as received from the backend.
 * @returns The extracted contract name, or `rawTitle` unchanged if no known pattern matches.
 */
function extractContractName(rawTitle: string): string {
  const englishMatch = rawTitle.match(/^Contract\s+(.+?)\s+entering notice window$/i);
  if (englishMatch?.[1]) {
    return englishMatch[1].trim();
  }

  const dutchMatch = rawTitle.match(/^Contract\s+(.+?)\s+in opzegvenster$/i);
  if (dutchMatch?.[1]) {
    return dutchMatch[1].trim();
  }

  const englishPendingMatch = rawTitle.match(/^Contract\s+(.+?)\s+awaiting signature$/i);
  if (englishPendingMatch?.[1]) {
    return englishPendingMatch[1].trim();
  }

  const dutchPendingMatch = rawTitle.match(/^Contract\s+(.+?)\s+wacht op handtekening$/i);
  if (dutchPendingMatch?.[1]) {
    return dutchPendingMatch[1].trim();
  }

  const rentalMatch = rawTitle.match(/^Huurcontract\s+(.+?)\s+nadert einddatum$/i);
  if (rentalMatch?.[1]) {
    return rentalMatch[1].trim();
  }

  const rentalNoticeMatch = rawTitle.match(/^Huurcontract\s+(.+?)\s+nadert opzegperiode$/i);
  if (rentalNoticeMatch?.[1]) {
    return rentalNoticeMatch[1].trim();
  }

  return rawTitle;
}

/**
 * Extracts the asset tag from a backend-generated notification title (see {@link extractContractName}).
 *
 * @param rawTitle - The raw notification title as received from the backend.
 * @returns The extracted asset tag, or `rawTitle` unchanged if no known pattern matches.
 */
function extractAssetTag(rawTitle: string): string {
  const englishMatch = rawTitle.match(/^Asset\s+(.+?)\s+lease end approaching$/i);
  if (englishMatch?.[1]) {
    return englishMatch[1].trim();
  }

  const dutchMatch = rawTitle.match(/^Lease van asset\s+(.+?)\s+loopt bijna af$/i);
  if (dutchMatch?.[1]) {
    return dutchMatch[1].trim();
  }

  return rawTitle;
}

/**
 * Builds a localized, human-readable title for a notification.
 *
 * Known trigger types are rendered from i18n templates (re-using the entity name/tag parsed out
 * of the backend's raw title); unrecognized trigger types fall back to the raw title as-is.
 *
 * @param notification - The notification to render a title for.
 * @param t - i18next translation function.
 * @returns The localized notification title.
 */
export function getNotificationTitle(notification: NotificationItem, t: TFunction): string {
  switch (notification.triggerType) {
    case 'ContractNoticeLeadTime':
      return t('notifications.generated.contractNoticeTitle', { name: extractContractName(notification.title) });
    case 'ContractPendingSignature':
      return t('notifications.generated.contractPendingSignatureTitle', { name: extractContractName(notification.title) });
    case 'RentalContractEndApproaching':
      return t('notifications.generated.rentalContractTitle', { name: extractContractName(notification.title) });
    case 'AssetLeaseExpiring':
      return t('notifications.generated.assetLeaseTitle', { name: extractAssetTag(notification.title) });
    case 'ExpenseClaimSubmitted':
      return t('notifications.generated.expenseClaimTitle');
    default:
      return notification.title;
  }
}

/**
 * Builds a localized, human-readable body message for a notification.
 *
 * Known trigger types are rendered from i18n templates including the due date and the
 * entity name/tag parsed out of the backend's raw title; unrecognized trigger types (and
 * `ExpenseClaimSubmitted`, which the backend already formats fully) fall back to the raw message.
 *
 * @param notification - The notification to render a message for.
 * @param t - i18next translation function.
 * @returns The localized notification message.
 */
export function getNotificationMessage(notification: NotificationItem, t: TFunction): string {
  switch (notification.triggerType) {
    case 'ContractNoticeLeadTime':
      return t('notifications.generated.contractNoticeMessage', {
        date: formatBelgianDate(notification.dueDate),
        name: extractContractName(notification.title),
      });
    case 'ContractPendingSignature':
      return t('notifications.generated.contractPendingSignatureMessage', {
        date: formatBelgianDate(notification.dueDate),
        name: extractContractName(notification.title),
      });
    case 'RentalContractEndApproaching':
      return t('notifications.generated.rentalContractMessage', {
        date: formatBelgianDate(notification.dueDate),
        name: extractContractName(notification.title),
      });
    case 'AssetLeaseExpiring':
      return t('notifications.generated.assetLeaseMessage', {
        date: formatBelgianDate(notification.dueDate),
        name: extractAssetTag(notification.title),
      });
    case 'ExpenseClaimSubmitted':
      return notification.message;
    default:
      return notification.message;
  }
}
